import React, { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './StaticNoiseCollageCard.css';
import { getImagePaths, getBestImagePath, shouldUseThumbnail } from '../../../utils/imageUtils';
import { startNoise, pickNoiseColor } from '../../../utils/noise';

interface StaticNoiseCollageCardProps {
    id: string;
    title: string;
    date: string;
    image: string;
}

const StaticNoiseCollageCard: React.FC<StaticNoiseCollageCardProps> = ({
    id,
    title,
    date,
    image
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // Holds the stop function for the currently running animation. The
    // previous version tracked a raw frame id and only ever cancelled the
    // most recent one, so sweeping the mouse across the grid left loops
    // running with nothing to stop them.
    const stopRef = useRef<(() => void) | null>(null);

    const stop = useCallback(() => {
        stopRef.current?.();
        stopRef.current = null;
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, []);

    // Always clean up if the card unmounts mid-animation.
    useEffect(() => stop, [stop]);

    const start = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        stopRef.current?.();
        stopRef.current = startNoise(canvas, { color: pickNoiseColor() });
    }, []);

    const paths = getImagePaths(image);
    const imagePath = getBestImagePath(paths, shouldUseThumbnail('grid'));

    return (
        <Link
            to={`/collages/${id}`}
            className="static-noise-collage-card"
            onMouseEnter={start}
            onMouseLeave={stop}
            onFocus={start}
            onBlur={stop}
        >
            <div className="card-image-container">
                <img
                    src={imagePath}
                    alt={title}
                    className="card-image"
                    loading="lazy"
                    decoding="async"
                />
                {/* Decorative only — the image underneath carries the meaning. */}
                <canvas
                    ref={canvasRef}
                    className="card-canvas"
                    width={300}
                    height={300}
                    aria-hidden="true"
                />
            </div>
            <div className="card-info">
                <div className="card-main-info">
                    <h2>{title}</h2>
                </div>
                <div className="card-date">{date}</div>
            </div>
        </Link>
    );
};

export default StaticNoiseCollageCard;
